import { meta, rows } from '@/db/database'
import { requireDevice } from '@/lib/device'
import { ean13CheckDigit } from '@/services/scan'

/**
 * In-store barcodes (POS.md §18.8): EAN-13 `<prefix>` + terminal `unique_no` (3) + device counter (7) + check digit.
 * `20` = products without a manufacturer code, `24` = customer cards. Unique across devices without the server, as the
 * terminal number is part of the code. A relinked device continues after the highest code of its terminal already
 * here (pulled), never from zero.
 */
export type InStorePrefix = '20' | '24'

export const inStoreCode = (prefix: InStorePrefix, terminalNo: number, counter: number) => {
  const body = `${prefix}${String(terminalNo % 1000).padStart(3, '0')}${String(counter).padStart(7, '0')}`
  return body + ean13CheckDigit(body)
}

const usedCodes = async (prefix: InStorePrefix): Promise<string[]> =>
  prefix === '20'
    ? (await rows('product_variations').toArray()).flatMap((v) => (Array.isArray(v.barcodes) ? v.barcodes.map(String) : []))
    : (await rows('customers').toArray()).map((c) => String(c.barcode ?? ''))

export const nextInStoreCode = async (prefix: InStorePrefix) => {
  const device = requireDevice()
  const head = `${prefix}${String(device.terminal_unique_no % 1000).padStart(3, '0')}`
  const highest = (await usedCodes(prefix))
    .filter((code) => code.length === 13 && code.startsWith(head))
    .reduce((max, code) => Math.max(max, Number(code.slice(5, 12)) || 0), 0)
  const key = `instore_counter.${prefix}`
  const counter = Math.max((await meta.get<number>(key)) ?? 0, highest) + 1
  await meta.set(key, counter)
  return inStoreCode(prefix, device.terminal_unique_no, counter)
}
